import imageCompression from 'browser-image-compression';

/**
 * Target dimensions for activity images
 */
export const IMAGE_DIMENSIONS = {
  width: 382,
  height: 197,
  aspectRatio: 382 / 197, // ~1.94:1
};

/**
 * File size threshold for compression (500KB)
 */
export const MAX_FILE_SIZE_KB = 500;

/**
 * Allowed image MIME types
 * Narrowed to png/webp only for transparent-background play-activity images
 */
export const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/webp',
] as const;

/**
 * Get file extension from MIME type
 */
export function getFileExtension(mimeType: string): string {
  const mapping: Record<string, string> = {
    'image/png': 'png',
    'image/webp': 'webp',
  };
  return mapping[mimeType] || 'png';
}

/**
 * Validate image file type
 */
export function validateImageType(file: File): boolean {
  return ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number]);
}

/**
 * Get error message for invalid file type
 */
export function getFileTypeError(file: File): string {
  if (!validateImageType(file)) {
    return `Invalid file type: ${file.type}. Allowed types: png, webp`;
  }
  return '';
}

/**
 * Create an image element from a file
 */
export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });
}

/**
 * Crop image based on crop area
 */
export async function getCroppedImage(
  imageSrc: string,
  croppedAreaPixels: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Set canvas size to target dimensions
  canvas.width = IMAGE_DIMENSIONS.width;
  canvas.height = IMAGE_DIMENSIONS.height;

  // Draw cropped image
  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    IMAGE_DIMENSIONS.width,
    IMAGE_DIMENSIONS.height
  );

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95
    );
  });
}

/**
 * Compress image if larger than threshold
 */
export async function compressImageIfNeeded(blob: Blob): Promise<Blob> {
  const fileSizeKB = blob.size / 1024;

  // If file is smaller than threshold, return as-is
  if (fileSizeKB <= MAX_FILE_SIZE_KB) {
    return blob;
  }

  // Convert blob to file for compression library
  const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });

  // Compress image
  const compressedFile = await imageCompression(file, {
    maxSizeMB: MAX_FILE_SIZE_KB / 1024,
    maxWidthOrHeight: IMAGE_DIMENSIONS.width,
    useWebWorker: true,
    fileType: 'image/jpeg',
  });

  return compressedFile;
}

/**
 * Upload blob to S3 using signed URL
 */
export async function uploadToS3(uploadUrl: string, blob: Blob, contentType = 'image/jpeg'): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: {
      'Content-Type': contentType,
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
}

/**
 * Upload blob to S3 with retry logic
 */
export async function uploadToS3WithRetry(
  uploadUrl: string,
  blob: Blob,
  maxRetries = 3,
  contentType = 'image/jpeg'
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await uploadToS3(uploadUrl, blob, contentType);
      return; // Success
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Upload failed');
      
      if (attempt < maxRetries) {
        // Wait before retry (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw new Error(
    `Failed to upload after ${maxRetries} attempts: ${lastError?.message}`
  );
}
